import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { Bell, AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import type { Notification } from '@/types/schemas';

export function NotificationsBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async (): Promise<Notification[]> => [],
    enabled: false,
  });

  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
    const unsub = onSnapshot(q, (snap) => {
      const items: Notification[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Notification, 'id'>),
      }));
      qc.setQueryData(['notifications'], items);
    }, () => {
      // ignore permission errors before role is set
    });
    return () => unsub();
  }, [qc]);

  const unreadCount = (data ?? []).filter((n) => !n.read).length;

  async function markAllRead() {
    const unread = (data ?? []).filter((n) => !n.read);
    await Promise.all(
      unread.map((n) => updateDoc(doc(db, 'notifications', n.id), { read: true })),
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] max-w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={markAllRead}
            >
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {(data ?? []).length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              You're all caught up.
            </div>
          )}
          {(data ?? []).map((n) => {
            const filterQs =
              n.type === 'low_stock' ? '?filter=low' : '';
            return (
              <a
                key={n.id}
                href={`/datahub${filterQs}`}
                className="block border-b px-3 py-2 last:border-b-0 hover:bg-accent"
              >
                <div className="flex items-start gap-2">
                  {n.type === 'low_stock' && (
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-amber-600" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium">{n.title}</span>
                      {!n.read && (
                        <Badge variant="warning" className="px-1.5 py-0 text-[10px]">
                          new
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{n.body}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground/70">
                      {formatDate(n.createdAt)}
                    </p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
